import { bindActionCreators } from 'redux';
import { connect, Dispatch } from 'react-redux';
import { Map } from 'immutable';
import cx from 'classnames';
import invariant from 'invariant';
import * as React from 'react';
// @ts-ignore
import { CSSTransitionGroup } from 'react-transition-group';

import { removeMembers, unselectAllMembership, loadMembership } from 'reducers/peopleTables';
// @ts-ignore
import BatchRoleCorrectionModal from 'users/components/BatchRoleCorrectionModal';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Glyph from 'components/Glyph';
import Group from 'entities/Group';
import Organization from 'entities/Organization';
import GroupMembership from 'entities/GroupMembership';
import ModalTrigger from 'components/ModalTrigger';
// @ts-ignore
import RemoveFromOrgModal from 'organizations/components/RemoveFromOrgModal';
// @ts-ignore
import RequestTranscriptModal from 'organizations/components/RequestTranscriptModal';
import Text from 'components/Text';

import { LoadMembershipOptions } from 'reducers/peopleTables';
import { GlyphName } from '@remind/r101-glyphs';
import { Backbone$Id } from 'types/typescript/Backbone';

import './styles/PeopleTableSuperActions.scss';

function ActionButton(props: {
  glyph: GlyphName,
  children: React.ReactNode,
  eventElementName: string,
  disabled: boolean,
}) {
  // rest is stuff coming from ModalTrigger
  const { glyph, children, eventElementName, disabled, ...rest } = props;

  const tooltipCopy: { [key: string]: string; } = {
    'bar-change-role':
      'You are unable to change the role of an administrator. Please select another user.',
    'bar-remove-from-school':
      'You are unable to remove an administrator from a school. Please select another user.',
  };

  const tooltip = props.disabled ? tooltipCopy[props.eventElementName] : undefined;

  return (
    <Button
      {...rest}
      size="xsmall"
      color="white"
      level="secondary"
      tooltip={tooltip}
      tooltipPlacement="top"
      disabled={disabled}
      className="PeopleTableSuperActions-item"
      eventElementName={eventElementName}>
      <Glyph name={glyph} size="large" />
      <Text size="xsmall">{children}</Text>
    </Button>
  );
}

type Props = {
  group: Group,
  className?: string,

  /* redux props */
  selectedMemberships: Map<number, GroupMembership>,
  removeMembers: (groupId: Backbone$Id, userIds: Backbone$Id[]) => any,
  unselectAllMembership: (groupId: Backbone$Id) => any,
  loadMembership: (
    groupId: Backbone$Id,
    userId: Backbone$Id,
    options: LoadMembershipOptions
  ) => any,
};

class PeopleTableSuperActions extends React.Component<Props> {
  shouldRenderAction = (permission: string) => {
    return this.props.selectedMemberships.every(groupMembership =>
      !!(groupMembership && groupMembership.hasPermission(permission))
    );
  };

  renderRequestTranscriptModal = (organization: Organization) => {
    const { selectedMemberships } = this.props;

    const users = selectedMemberships
      .valueSeq()
      .toArray()
      .map(m => {
        return {
          id: m.getUserId(),
          fullName: m.getUser().getFullName(),
          firstName: m.getUser().getFirstName(),
          canGetMessageHistory: m.hasPermission('can_get_message_history'),
          canRequestMessageHistory: m.hasPermission('can_request_message_history'),
        };
      });

    return <RequestTranscriptModal users={users} organizationId={organization.id} />;
  };

  unselectAllMembership = () => {
    this.props.unselectAllMembership(this.props.group.id);
  };
  loadGroupMembership = (userId: Backbone$Id) => {
    this.props.loadMembership(this.props.group.id, Number(userId), {
      update: true,
    });
  };
  removeAllSelected = () => {
    const ids = this.props.selectedMemberships
      .valueSeq()
      .toArray()
      .map(m => m.getUserId());
    this.props.removeMembers(this.props.group.id, ids);
  };
  renderRemoveFromOrgModal = (organization: Organization) => {
    const { selectedMemberships } = this.props;
    const userIds = selectedMemberships
      .valueSeq()
      .toArray()
      .map(m => m.getUserId());

    return (
      <RemoveFromOrgModal
        userIds={userIds}
        orgId={organization.id}
        onRemoved={this.removeAllSelected}
        orgName={organization.getName()}
      />
    );
  };

  renderChangeRolesModal = (organization: Organization) => {
    const { selectedMemberships } = this.props;

    return (
      <BatchRoleCorrectionModal
        organization={organization.toJSON()}
        groupMemberships={selectedMemberships.valueSeq().toArray()}
        onUserRoleSave={this.loadGroupMembership}
      />
    );
  };

  renderActions() {
    const { selectedMemberships, group } = this.props;
    const selectedCount = selectedMemberships.size;

    if (selectedCount === 0) {
      return null;
    }

    const organization = group.getOrganization();

    const moreThanOneSelected = selectedCount > 1;

    const changeRolesModal = organization && this.renderChangeRolesModal(organization);
    const requestTranscriptModal = organization && this.renderRequestTranscriptModal(organization);
    const removeFromOrgModal = organization && this.renderRemoveFromOrgModal(organization);

    return (
      <div key="actions" className={cx('PeopleTableSuperActions', this.props.className)}>
        <Buttons>
          {changeRolesModal && <ModalTrigger modal={changeRolesModal}>
            <ActionButton
              glyph="avatar"
              eventElementName="bar-change-role"
              disabled={!this.shouldRenderAction('can_change_role')}>
              {moreThanOneSelected ? 'Change roles' : 'Change role'}
            </ActionButton>
          </ModalTrigger>}
          {requestTranscriptModal && <ModalTrigger modal={requestTranscriptModal}>
            <ActionButton
              glyph="email-transcript"
              eventElementName="bar_message_history"
              disabled={!this.shouldRenderAction('can_request_message_history')}>
              Get message history
            </ActionButton>
          </ModalTrigger>}
          {group.isStaffClass() && removeFromOrgModal && (
            <ModalTrigger modal={removeFromOrgModal}>
              <ActionButton
                glyph="close"
                eventElementName="bar-remove-from-school"
                disabled={!this.shouldRenderAction('can_remove_from_org')}>
                Remove from school
              </ActionButton>
            </ModalTrigger>
          )}
        </Buttons>

        <div>
          <Text color="white" className="PeopleTableSuperActions-numberInfo">
            {moreThanOneSelected
              ? `${selectedCount} people selected`
              : `${selectedCount} person selected`}
          </Text>
          <Button
            eventElementName="bar_deselect_all"
            size="xsmall"
            color="white"
            level="secondary"
            onClick={this.unselectAllMembership}>
            Deselect all
          </Button>
        </div>
      </div>
    );
  }

  render() {
    return (
      <CSSTransitionGroup
        transitionName="PeopleTableSuperActions-transition"
        transitionEnterTimeout={300}
        transitionLeaveTimeout={300}>
        {this.renderActions()}
      </CSSTransitionGroup>
    );
  }
}

function mapStateToProps(state: any, props: Props) {
  const table = state.peopleTables.get(props.group.id);

  return {
    selectedMemberships: table ? table.selectedMemberships : Map<number, GroupMembership>(),
  };
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
  // $FlowIssue
  return bindActionCreators({ removeMembers, unselectAllMembership, loadMembership }, dispatch);
}

export { PeopleTableSuperActions };
// $FlowIssue
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PeopleTableSuperActions);
